// setup for polyfills
import "core-js/actual/promise/with-resolvers"; // needed for chrome < 118, important for PDF.JS
import "core-js/actual/url/parse";
import "core-js/actual/array";
import "core-js/actual/structured-clone";

import { installAbortSignalPolyfill } from "abort-signal-polyfill";
if (!AbortSignal.any) {
  installAbortSignalPolyfill();
}

// other imports
import App from "@/App.vue";
import clickOutside from "@/directives/clickOutside";
import { auth } from "@/fire";
import "@/fonts.css";
import graphqlClient from "@/graphql/client";
import "@/main.scss";
import "@/styles/shared-controls.css";
import router from "@/router";
import { syncClassicThemeForInitialPath } from "@/router/classicTheme";
import { store } from "@/store";
import { i18n } from "@/translations";
import { AnalyticsService, EventType, Events } from "@/util";
import * as Sentry from "@sentry/vue";
import urql from "@urql/vue";
import { useIdle } from "@vueuse/core";
import { onAuthStateChanged } from "firebase/auth";
import { createPinia } from "pinia";
import { createApp, watch } from "vue";
import { notifyWhenUserReady } from "./init";

//import "core-js/actual";

syncClassicThemeForInitialPath(window.location.pathname, (pathname) =>
  router.resolve(pathname),
);

const vueApp = createApp(App);
const pinia = createPinia();

vueApp.use(store);
vueApp.use(pinia);
vueApp.use(urql, graphqlClient);
vueApp.use(clickOutside);
vueApp.use(i18n);

try {
  Sentry.init({
    app: vueApp,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // This path is also hardcoded in backend/src/index.ts (POST /bugs route)
    // and in the WAF exclusion rule 10001 in
    // proxy/crs/REQUEST-900-EXCLUSION-RULES-BEFORE-CRS.conf — keep in sync.
    tunnel: "/backend/bugs",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });
} catch (e) {
  console.error(e);
}

vueApp.use(router);
vueApp.mount("#app");

let intervalId: number | null = null;

const startInterval = () => {
  clearIntervalIfExists();
  intervalId = window.setInterval(() => {
    AnalyticsService.sendEvent(Events.Alive);
  }, 8000);
};

const clearIntervalIfExists = () => {
  if (intervalId) {
    window.clearInterval(intervalId);
    intervalId = null;
  }
};

const { idle, lastActive } = useIdle(3 * 60 * 1000); // 3 min
watch(idle, () => {
  if (idle.value) {
    AnalyticsService.sendEvent(Events.UserIsIdle);
    clearIntervalIfExists();
  } else {
    AnalyticsService.sendEvent(Events.UserIsActive);
    startInterval();
  }
});

onAuthStateChanged(auth, async (user) => {
  if (user) {
    store.commit("setUserId", user.uid);
    AnalyticsService.logEvent(EventType.AppOpen);
  } else {
    store.commit("setUserId", null);
  }
});

const visibilityChangeHandler = () => {
  if (document.visibilityState === "hidden") {
    AnalyticsService.sendEvent(Events.PauseApp);
    clearIntervalIfExists();
  } else {
    AnalyticsService.sendEvent(Events.ResumeApp);
    startInterval();
  }
};

const beforeUnloadHandler = () => {
  AnalyticsService.sendEvent(Events.CloseApp);
  document.removeEventListener("visibilitychange", visibilityChangeHandler);
  window.removeEventListener("beforeunload", beforeUnloadHandler);
  clearIntervalIfExists();
};

document.addEventListener("visibilitychange", visibilityChangeHandler);
window.addEventListener("beforeunload", beforeUnloadHandler);

notifyWhenUserReady().then(() => {
  AnalyticsService.sendEvent(Events.StartApp);
  startInterval();
});
